import { TRADES } from './subcontractors';
import { formatMoney } from './format';

/* The application to work with E3 (Duncan 2026-10-27): E3's Subcontractor Qualification Form (Microsoft Forms, 31
   questions) moved into Summit -- answered at workwith.e3es.com, reviewed in Subcontractors → Applications by the
   people given "Sub Applications". The form's questions in its order, a few made easier to answer: the business address
   in parts (its ZIP sets the region), coverage as amounts checked against E3's minimums, trades from Summit's list,
   references as company, contact and phone, "which E3 project" typed in (Duncan's pick); and, Duncan 2026-10-28, whether
   they'd raise coverage that's under E3's minimum. */
export const MINIMUMS = { gl_coverage: 2000000, auto_coverage: 1000000, umbrella_coverage: 1000000 };
// The umbrella for high-hazard work (the Subcontract Work Order's Exhibit C.1: "steel erection, roofing, masonry, etc."):
// Duncan 2026-10-28, roofers are held to it -- Roofing being the one of those on the trade list.
export const HIGH_HAZARD_UMBRELLA = 3000000;
export const HIGH_HAZARD_TRADES = ['Roofing'];
export { TRADES };

export const SECTIONS = [
  {
    key: 'company', title: 'Your company', fields: [
      { key: 'firm_name', label: 'Name of firm', brief: 'Firm', required: true },
      { key: 'contact_name', label: 'Contact', brief: 'Contact', placeholder: 'First and last name', required: true },
      { key: 'address_line1', label: 'Business address', brief: 'Address', placeholder: 'Street', required: true, wide: true },
      { key: 'city', label: 'City', brief: 'City', required: true },
      { key: 'state', label: 'State', brief: 'State', kind: 'state', required: true, short: true },
      { key: 'zip', label: 'ZIP', brief: 'ZIP', kind: 'zip', required: true, short: true },
      { key: 'phone', label: 'Phone', brief: 'Phone', kind: 'phone', required: true },
      { key: 'email', label: 'Email', brief: 'Email', kind: 'email', required: true },
      { key: 'website', label: 'Company website', brief: 'Website', placeholder: 'www.example.com' },
      { key: 'license_no', label: 'License number', brief: 'License number', placeholder: 'e.g. TACLB11907C' },
    ],
  },
  {
    key: 'insurance', title: 'Insurance', note: 'Your coverage today. E3’s minimum is under each.', fields: [
      { key: 'gl_coverage', label: 'Commercial General Liability', brief: 'General liability', kind: 'money', hint: 'E3 requires $2,000,000 general aggregate', required: true },
      { key: 'auto_coverage', label: 'Automobile Liability', brief: 'Auto liability', kind: 'money', hint: 'E3 requires $1,000,000', required: true },
      { key: 'umbrella_coverage', label: 'Umbrella / Excess Liability', brief: 'Umbrella / excess', kind: 'money', hint: 'E3 requires $1,000,000 each occurrence and $1,000,000 aggregate; $3,000,000 each occurrence for high-hazard work (steel erection, roofing, masonry)', required: true },
      { key: 'increase_coverage', label: 'Are you willing to increase your insurance coverage to meet the minimum if it currently does not?', brief: 'Will raise coverage to E3’s minimum', kind: 'yesno', required: true, wide: true },
      { key: 'workers_comp', label: 'Do you carry full Workers’ Compensation coverage?', brief: 'Full workers’ comp', kind: 'yesno', required: true, wide: true },
    ],
  },
  {
    key: 'agreements', title: 'Our agreements', fields: [
      { key: 'msa_reviewed', label: 'Have you reviewed our Master Service Agreement?', brief: 'Read the MSA', kind: 'yesno', link: 'msa', required: true, wide: true },
      { key: 'subcontract_reviewed', label: 'Have you reviewed our Subcontract Agreement?', brief: 'Read the Subcontract Agreement', kind: 'yesno', link: 'subcontract', required: true, wide: true },
    ],
  },
  {
    key: 'work', title: 'Your work', fields: [
      { key: 'trades', label: 'Trades you do', brief: 'Trades', kind: 'trades', required: true, wide: true },
      { key: 'scope', label: 'Briefly describe the work you normally do', brief: 'Usual work', kind: 'text', placeholder: 'e.g. HVAC renovation, lighting installation, duct fabrication', required: true, wide: true },
      { key: 'years_in_business', label: 'Years in business under your current name', brief: 'Years in business', kind: 'number' },
      { key: 'employees', label: 'Average number of employees', brief: 'Employees', kind: 'number' },
      { key: 'largest_contract', label: 'Largest contract you’ve done', brief: 'Largest contract', kind: 'money' },
      { key: 'smallest_contract', label: 'Smallest contract you’ve done', brief: 'Smallest contract', kind: 'money' },
      { key: 'average_contract', label: 'Your average contract', brief: 'Average contract', kind: 'money' },
      { key: 'backlog', label: 'Your current 12-month backlog', brief: '12-month backlog', kind: 'money' },
      { key: 'subcontracted_trades', label: 'Trades you usually subcontract', brief: 'Trades they subcontract', placeholder: 'e.g. a mechanical contractor subcontracting electrical', wide: true },
      { key: 'labor_outsourced', label: 'How much of your labor is outsourced?', brief: 'Labor outsourced', placeholder: 'e.g. 30–35%' },
      { key: 'travel', label: 'How far will you travel for a project?', brief: 'Will travel', placeholder: 'e.g. 300 miles from our office' },
      { key: 'union', label: 'Union affiliated?', brief: 'Union', kind: 'yesno' },
      { key: 'hub', label: 'HUB certified?', brief: 'HUB certified', kind: 'yesno' },
      { key: 'bonding', label: 'Bonding capacity', brief: 'Bonding capacity', placeholder: 'e.g. $1,000,000 single project, $2,000,000 aggregate', wide: true },
    ],
  },
  { key: 'references', title: 'References', note: 'Owners or clients you’ve worked for directly in the past 2 years.', kind: 'references' },
  {
    key: 'interest', title: 'E3 projects', fields: [
      { key: 'project_interest', label: 'Which E3 project are you interested in working on?', brief: 'Wants to work on', placeholder: 'The project, school district or city', required: true, wide: true },
    ],
  },
  { key: 'documents', title: 'Documents', note: 'A PDF, a photo or a Word file, 10 MB at most each.', kind: 'files' },
];

export const FILES = [
  { key: 'w9', label: 'Your W-9' },
  { key: 'safety', label: 'Your company’s safety plan', hint: 'For your trade, with your safety rating and record, including days without lost time.' },
];
export const REFERENCE_COUNT = 3;
export const FILE_MAX = 10 * 1024 * 1024;
export const FILE_TYPES = '.pdf,.jpg,.jpeg,.png,.heic,.heif,.doc,.docx';

/** Every question by its key, for the answers' labels in review. */
export const FIELD = Object.fromEntries(SECTIONS.flatMap((s) => (s.fields || []).map((f) => [f.key, f])));

/** Whole dollars, "$2,000,000"; an amount typed with $ and commas reads as its number. */
export const moneyText = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? '' : `$${Math.round(Number(v)).toLocaleString('en-US')}`);
export const moneyOf = (s) => {
  const n = Number(String(s == null ? '' : s).replace(/[$,\s]/g, ''));
  return String(s == null ? '' : s).trim() && Number.isFinite(n) ? n : null;
};

const isHighHazard = (a) => (a.trades || []).some((t) => HIGH_HAZARD_TRADES.includes(t));
/** E3's minimum for a coverage, for these answers: a roofer's umbrella is held to the high-hazard one. */
export const minimumOf = (k, a) => (k === 'umbrella_coverage' && isHighHazard(a) ? HIGH_HAZARD_UMBRELLA : MINIMUMS[k]);

const AGREEMENT_READ = [['msa_reviewed', 'the MSA'], ['subcontract_reviewed', 'the Subcontract Agreement']];

/** The things an application is judged on, always these and in this order (Duncan 2026-10-28: the list shows the
    same checks for every application; then "one more flag is that if they have entered a license number"): { key,
    label, tone: 'ok' | 'warn' | 'bad', lines: what was found, fields: { question key: tone } for the answers behind a
    problem }. Coverage under E3's minimum is amber, red when they won't raise it; no full workers' comp is red; an
    agreement not read, a W-9 or safety plan not sent, or no license number given, amber. */
export function checksOf(app) {
  const a = app.answers || {};
  const files = app.files || {};
  const check = (key, label, tone, lines, fields = {}) => ({ key, label, tone, lines, fields });

  const coverage = [['gl_coverage', 'General liability'], ['auto_coverage', 'Auto liability'], ['umbrella_coverage', 'Umbrella']];
  const short = coverage.filter(([k]) => { const v = moneyOf(a[k]); return v != null && v < minimumOf(k, a); });
  const missing = coverage.filter(([k]) => moneyOf(a[k]) == null);
  const wont = short.length > 0 && a.increase_coverage === 'no';
  const insurance = [
    ...short.map(([k, label]) => `${label} ${formatMoney(moneyOf(a[k]))}; E3 needs ${formatMoney(minimumOf(k, a))}${k === 'umbrella_coverage' && isHighHazard(a) ? ' for roofing' : ''}.`),
    ...missing.map(([, label]) => `No amount given for ${label.toLowerCase()}.`),
    ...(short.length && a.increase_coverage ? [wont ? 'Won’t raise its coverage to E3’s minimum.' : 'Will raise its coverage to E3’s minimum.'] : []),
  ];
  const insuranceTone = wont ? 'bad' : insurance.length ? 'warn' : 'ok';

  const read = AGREEMENT_READ.filter(([k]) => a[k] === 'yes').map(([, name]) => name);
  const unread = AGREEMENT_READ.filter(([k]) => a[k] === 'no');
  const noFiles = [['w9', 'No W-9.'], ['safety', 'No safety plan.']].filter(([k]) => !files[k]).map(([, text]) => text);

  return [
    check('insurance', 'Insurance', insuranceTone, insurance.length ? insurance : ['Meets E3’s minimums.'], {
      ...Object.fromEntries(short.map(([k]) => [k, insuranceTone])),
      ...Object.fromEntries(missing.map(([k]) => [k, 'warn'])),
      ...(wont ? { increase_coverage: 'bad' } : {}),
    }),
    a.workers_comp === 'yes' ? check('workers_comp', 'Workers’ comp', 'ok', ['Carries full workers’ comp.'])
      : a.workers_comp === 'no' ? check('workers_comp', 'Workers’ comp', 'bad', ['Doesn’t carry full workers’ comp.'], { workers_comp: 'bad' })
        : check('workers_comp', 'Workers’ comp', 'warn', ['Didn’t say whether it carries workers’ comp.']),
    unread.length || !read.length
      ? check('agreements', 'Agreements', 'warn', [
        ...unread.map(([, name]) => `Hasn’t read ${name}.`),
        ...(read.length ? [`Read ${read.join(' and ')}.`] : unread.length ? [] : ['Didn’t say whether it read the agreements.']),
      ], Object.fromEntries(unread.map(([k]) => [k, 'warn'])))
      : check('agreements', 'Agreements', 'ok', [`Read ${read.join(' and ')}.`]),
    noFiles.length ? check('documents', 'Documents', 'warn', noFiles) : check('documents', 'Documents', 'ok', ['W-9 and safety plan sent.']),
    String(a.license_no || '').trim()
      ? check('license', 'License', 'ok', [`License ${String(a.license_no).trim()}.`])
      : check('license', 'License', 'warn', ['No license number given.'], { license_no: 'warn' }),
  ];
}

export const STATUSES = [['new', 'New'], ['reviewing', 'Reviewing'], ['approved', 'Approved'], ['declined', 'Declined']];

// The agreements the form links, uploaded and replaced on the Applications page; a question about one that isn't on
// file stays off the form (there's nothing to read).
export const AGREEMENT_KINDS = [['msa', 'Master Service Agreement'], ['subcontract', 'Subcontract Agreement']];
